import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Instagram, Phone, Mail, Loader2, Linkedin, Info } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/components/Layout";
import { RevealLines, Reveal } from "@/components/motion-primitives";
import { SectionLabel } from "@/components/ui-blocks";
import { SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", objective: "", coaching_type: "salle", bilan_type: "salle", message: "", consent: false, company: "" };
const field = "w-full bg-transparent border-b border-border py-3 text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:border-accentbrand transition-colors";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  useSeo(c.seoTitle, c.seoDesc);

  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.objective || !form.message) {
      toast.error(c.required);
      return;
    }
    if (!form.consent) {
      toast.error(c.consentError);
      return;
    }
    setSending(true);
    try {
      const coaching_type = form.bilan_type === "visio" ? "ligne" : "salle";
      await axios.post(`${API}/contact`, { ...form, coaching_type });
      setSent(true);
      toast.success(c.successTitle);
    } catch (err) {
      toast.error(c.errorSend);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-8 lg:pb-10">
        <SectionLabel>{c.label}</SectionLabel>
        <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={c.heroLines} />
        <Reveal delay={0.4}><p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{c.sub}</p></Reveal>
        <Reveal delay={0.45}><p className="mt-2 text-sm font-mono-alt uppercase tracking-wider text-accentbrand">{c.responseTime}</p></Reveal>
      </section>

      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-14">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div key="success" data-testid="contact-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-border bg-card p-8 lg:p-10">
                <CheckCircle2 className="text-accentbrand" size={44} />
                <h2 className="mt-5 font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-ink">{c.successTitle}</h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">{c.successText.replace("{name}", form.first_name)}</p>
                <button data-testid="contact-reset" onClick={() => { setForm(EMPTY); setSent(false); }} className="mt-6 text-sm font-bold uppercase tracking-wide text-accentbrand hover:underline">
                  {c.another}
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" data-testid="contact-form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <input type="text" name="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.firstName} *</label>
                    <input data-testid="input-first-name" className={field} value={form.first_name} onChange={set("first_name")} placeholder={c.firstNamePh} />
                  </div>
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.lastName} *</label>
                    <input data-testid="input-last-name" className={field} value={form.last_name} onChange={set("last_name")} placeholder={c.lastNamePh} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.email} *</label>
                    <input data-testid="input-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder={c.emailPh} />
                  </div>
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.phone}</label>
                    <input data-testid="input-phone" className={field} value={form.phone} onChange={set("phone")} placeholder={c.phonePh} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.objective} *</label>
                  <select data-testid="select-objective" className={`${field} appearance-none cursor-pointer`} value={form.objective} onChange={set("objective")}>
                    <option value="" disabled>{c.objectivePh}</option>
                    {t.objectives.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.bilan} *</label>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {c.bilanOptions.map((opt) => (
                      <button type="button" key={opt.v} data-testid={`bilan-type-${opt.v}`} onClick={() => setForm((f) => ({ ...f, bilan_type: opt.v }))}
                        className={`py-4 px-4 border text-sm font-bold uppercase tracking-wide transition-colors ${form.bilan_type === opt.v ? "bg-night text-white border-ink" : "border-border text-ink hover:border-ink"}`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wide text-ink">{c.message} *</label>
                  <textarea data-testid="input-message" rows={4} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder={c.messagePh} />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-[#FF4500] shrink-0" />
                  <span className="text-sm text-muted-foreground leading-relaxed">{c.consent}</span>
                </label>
                <div className="flex items-start gap-3 bg-secondary/70 p-4 text-sm text-muted-foreground">
                  <Info size={16} className="text-accentbrand mt-0.5 shrink-0" /><p>{c.bilanMention}</p>
                </div>
                <button type="submit" data-testid="contact-submit" disabled={sending}
                  className="hover-arrow inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-accentbrand text-white text-sm font-bold uppercase tracking-wide px-8 py-4 min-h-[48px] hover:bg-[#E63E00] transition-colors disabled:opacity-60">
                  {sending ? <><Loader2 size={16} className="animate-spin" /> {c.sending}</> : <>{t.common.bilanOffert}</>}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-border bg-card p-6 lg:p-8">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-ink">{c.directTitle}</h2>
            <div className="mt-5 space-y-3">
              <a href={SITE.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="hover-arrow flex items-center gap-3 w-full bg-[#25D366] text-white px-5 py-4 font-bold uppercase tracking-wide text-sm">
                <Phone size={18} /> {t.common.whatsapp}
              </a>
              <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="flex items-center gap-3 py-3 border-b border-border text-ink hover:text-accentbrand transition-colors">
                <Instagram size={18} className="text-accentbrand" /> @coach_gi06
              </a>
              <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="flex items-center gap-3 py-3 border-b border-border text-ink hover:text-accentbrand transition-colors">
                <Linkedin size={18} className="text-accentbrand" /> {t.common.linkedin}
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-center gap-3 py-3 border-b border-border text-ink hover:text-accentbrand transition-colors break-all">
                <Mail size={18} className="text-accentbrand shrink-0" /> {SITE.email}
              </a>
            </div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">{c.asideNote}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
